import { useCallback, useContext, useEffect, useState } from 'react'
import api from '../../api'
import { CampaignContext, ProjectContext } from '../../contexts'
import { type CampaignDelivery as CampaignDeliveryType, CampaignSendState } from '../../types'
import Alert from '../../ui/Alert'
import Heading from '../../ui/Heading'
import Modal from '../../ui/Modal'
import { PreferencesContext } from '../../ui/PreferencesContext'
import { Table, useSearchTableQueryState } from '../../components'
import Tag, { TagVariant } from '../../ui/Tag'
import Button from '../../ui/Button'
import Chart from '../../ui/Chart'
import { DownloadIcon } from '../../ui/icons'
import DateRangePicker, { DateRange } from '../../ui/DateRangePicker'
import { formatDate } from '../../utils'
import { useRoute } from '../router'
import { Translation, useTranslation } from 'react-i18next'
import { campaignSendStates } from './CampaignFields'
import { SingleSelect } from '../../ui/form/SingleSelect'
import CampaignStats, { CampaignMetrics, parseSeries } from './CampaignStats'
import { toast } from 'react-hot-toast/headless'
import { trackExport } from '../exports/exportTracker'
import { isInAppBroadcast } from './inAppBroadcast'

const CampaignSendTag = ({ state }: { state: CampaignSendState }) => {
    const variant: Record<CampaignSendState, TagVariant | undefined> = {
        pending: 'info',
        throttled: 'warn',
        bounced: 'error',
        sent: 'success',
        failed: 'error',
        aborted: 'warn',
    }
    return <Tag variant={variant[state]}>
        <Translation>{ (t) => t(state) }</Translation>
    </Tag>
}

const userHeading = (label: string, filterState?: string, delivery?: CampaignDeliveryType, broadcast?: boolean) => {
    if (!filterState || !delivery) return label
    const counts: Record<string, number | undefined> = {
        sent: delivery.sent,
        ...!broadcast && { pending: delivery.pending },
    }
    const count = counts[filterState]
    return count != null ? `${label} (${count.toLocaleString()})` : label
}

export default function CampaignDelivery() {
    const [project] = useContext(ProjectContext)
    const { t } = useTranslation()
    const [preferences] = useContext(PreferencesContext)
    const [campaign, setCampaign] = useContext(CampaignContext)
    const { id, state, send_at, ends_at, delivery, progress } = campaign
    const broadcast = isInAppBroadcast(campaign)
    const sendStates = campaignSendStates(t).filter(option => !broadcast || option.key !== 'pending')
    const searchState = useSearchTableQueryState(
        useCallback(async params => await api.campaigns.users(project.id, id, params), [id, project]),
        {
            filter: { state: campaign.state === 'finished' || broadcast ? 'sent' : 'pending' },
        },
    )
    const route = useRoute()

    // Date range defaults based on campaign lifecycle
    const campaignEnded = ['finished', 'aborted'].includes(state)
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        const start = new Date(send_at ?? campaign.created_at)
        start.setHours(0, 0, 0, 0)
        let end: Date
        if (campaignEnded) {
            end = new Date(campaign.updated_at)
        } else {
            const monthLater = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000)
            const now = new Date()
            end = monthLater < now ? monthLater : now
        }
        // Cap at end of current hour so we don't generate empty future buckets
        const endOfCurrentHour = new Date()
        endOfCurrentHour.setMinutes(59, 59, 999)
        if (end > endOfCurrentHour) {
            end = endOfCurrentHour
        }
        return { start, end }
    })

    // Metrics state
    const [metrics, setMetrics] = useState<CampaignMetrics | undefined>()
    const [isExportOpen, setIsExportOpen] = useState(false)

    // Expanded metric modal state — stores a key into metrics so data stays live
    const [expandedMetric, setExpandedMetric] = useState<{ title: string, key: keyof CampaignMetrics } | undefined>()

    // Auto-refresh for loading/aborting campaigns
    useEffect(() => {
        const refresh = () => {
            api.campaigns.get(project.id, campaign.id)
                .then(setCampaign)
                .then(searchState.reload)
                .catch(() => {})
        }

        if (!['loading', 'aborting'].includes(state)) return
        const complete = progress?.complete ?? 0
        const total = progress?.total ?? 0
        const percent = total > 0 ? complete / total * 100 : 0
        const refreshRate = percent < 5 ? 1000 : 5000
        const interval = setInterval(refresh, refreshRate)
        refresh()

        return () => clearInterval(interval)
    }, [state])

    // Fetch time-series metrics
    useEffect(() => {
        if (state === 'draft') return
        const start = dateRange.start.toISOString()
        const end = dateRange.end.toISOString()

        api.campaigns.metrics(project.id, id, start, end)
            .then(result => setMetrics({
                sent: parseSeries(result.sent),
                deliveryRate: parseSeries(result.deliveryRate),
                opened: parseSeries(result.opened),
                clicked: parseSeries(result.clicked),
                unsubscribed: parseSeries(result.unsubscribed),
            }))
            .catch(() => {})
    }, [project.id, id, dateRange, state])

    const diffHours = (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60)
    const dateFormat = diffHours <= 12 ? 'h:mm aa' : diffHours <= 7 * 24 ? 'h:mm aa' : 'MMM d'

    const handleZoom = useCallback((start: Date, end: Date) => {
        setDateRange({ start, end })
    }, [])

    const exportState = searchState.params.filter?.state as CampaignSendState | undefined
    const handleExport = async () => {
        try {
            const record = await api.campaigns.export(project.id, id, exportState ? [exportState] : undefined)
            setIsExportOpen(false)
            trackExport(project.id, record)
        } catch (error: any) {
            toast.error(error.response?.data?.error ?? error.message)
            setIsExportOpen(false)
        }
    }

    return (
        <>
            <Heading title={t('delivery')} size="h3" actions={<DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                maxDate={new Date()}
            />} />
            {state !== 'draft'
                ? <>
                    {state === 'scheduled' && send_at
                        && <Alert title={t('scheduled')}>{t('campaign_alert_scheduled')} <strong>{formatDate(preferences, send_at)}</strong></Alert>
                    }
                    {broadcast && state === 'running' && ends_at
                        && <Alert variant="info" title={t('in_app_broadcast_running')}>{t('in_app_broadcast_running_desc')} <strong>{formatDate(preferences, ends_at)}</strong></Alert>
                    }
                    {delivery && (
                        <CampaignStats
                            broadcast={broadcast}
                            delivery={delivery}
                            state={state}
                            progress={progress}
                            metrics={metrics}
                            dateFormat={dateFormat}
                            onExpandMetric={(title, key) => setExpandedMetric({ title, key })}
                        />
                    )}
                    <Heading title={userHeading(t('users'), searchState.params.filter?.state, delivery, broadcast)} size="h4" actions={
                        <>
                            <SingleSelect
                                key="state"
                                size="small"
                                options={sendStates}
                                value={searchState.params.filter?.state}
                                onChange={value => searchState.setParams({
                                    ...searchState.params,
                                    filter: {
                                        ...searchState.params.filter,
                                        state: value.length ? value : undefined,
                                    },
                                })}
                                placeholder={t('state')}
                                toValue={v => v.key}
                            />
                            <Button
                                size="small"
                                variant="secondary"
                                icon={<DownloadIcon />}
                                onClick={() => setIsExportOpen(true)}
                            >{t('export')}</Button>
                        </>
                    } />
                    <Table
                        {...searchState}
                        columns={[
                            { key: 'full_name', title: t('name') },
                            { key: 'email', title: t('email') },
                            { key: 'phone', title: t('phone') },
                            {
                                key: 'state',
                                title: t('state'),
                                cell: ({ item: { state } }) => CampaignSendTag({ state }),
                            },
                            { key: 'send_at', title: t('send_at') },
                            { key: 'opened_at', title: t('opened_at') },
                        ]}
                        onSelectRow={({ id }) => route(`users/${id}`)}
                        emptyMessage={t('campaign_delivery_empty_filter')}
                    />
                    <Modal
                        open={isExportOpen}
                        onClose={() => setIsExportOpen(false)}
                        title={t('export_users')}
                        actions={
                            <>
                                <Button variant="secondary" onClick={() => setIsExportOpen(false)}>{t('cancel')}</Button>
                                <Button onClick={handleExport}>{t('export_users_confirm')}</Button>
                            </>
                        }
                    >
                        <p>{exportState
                            ? t('export_campaign_users_filtered_description', { state: t(exportState) })
                            : t('export_campaign_users_description')}</p>
                        <p>{t('export_snapshot_note')}</p>
                    </Modal>
                    <Modal
                        title={expandedMetric?.title ?? ''}
                        size="large"
                        open={!!expandedMetric}
                        onClose={() => setExpandedMetric(undefined)}
                    >
                        {expandedMetric && metrics?.[expandedMetric.key] && (
                            <Chart
                                series={metrics[expandedMetric.key]}
                                maxHeight={400}
                                dateFormat={dateFormat}
                                onZoom={handleZoom}
                                formatter={expandedMetric.key === 'deliveryRate' ? v => `${v}%` : undefined}
                            />
                        )}
                    </Modal>
                </>
                : <Alert variant="plain" title={t('pending')}>{t('campaign_alert_pending')}</Alert>
            }
        </>
    )
}
